// Authentification hybride : Firebase Auth + Firestore
import { doc, getDoc, setDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { signInWithEmailAndPassword, createUserWithEmailAndPassword, signOut } from 'firebase/auth';
import { db } from './firebaseFirestore';
import { auth } from './firebaseConfig';

// Fonction de connexion hybride : Firebase Auth + Firestore
export const loginWithEmailAndPassword = async (email, password) => {
  try {
    console.log('🔍 Tentative de connexion hybride pour:', email);
    
    // ÉTAPE 1: Connexion Firebase Auth
    console.log('📝 Étape 1: Connexion Firebase Auth...');
    let userCredential;
    try {
      userCredential = await signInWithEmailAndPassword(auth, email, password);
      console.log('✅ Firebase Auth réussi:', userCredential.user.uid);
    } catch (authError) {
      console.log('❌ Firebase Auth échoué:', authError.code);
      
      // Si l'utilisateur n'existe pas dans Firebase Auth, essayer de le créer
      if (authError.code === 'auth/user-not-found') {
        console.log('📝 Tentative de création d\'utilisateur Firebase Auth...');
        try {
          userCredential = await createUserWithEmailAndPassword(auth, email, password);
          console.log('✅ Utilisateur Firebase Auth créé:', userCredential.user.uid);
        } catch (createError) {
          console.log('❌ Création Firebase Auth échouée:', createError.code);
          return { success: false, error: 'Impossible de créer le compte. Vérifiez vos identifiants.' };
        }
      } else {
        return { success: false, error: 'Email ou mot de passe incorrect' };
      }
    }
    
    // ÉTAPE 2: Récupération des données Firestore
    console.log('📝 Étape 2: Récupération des données Firestore...');
    
    // Vérifier d'abord dans la collection 'users'
    const userDocRef = doc(db, 'users', email);
    const userDocSnap = await getDoc(userDocRef);
    
    if (userDocSnap.exists()) {
      const userData = userDocSnap.data();
      console.log('✅ Document users trouvé:', userData);
      
      // Vérifier le statut de l'utilisateur
      if (userData.status === 'inactive') {
        console.log('❌ Compte inactif détecté');
        // Déconnecter l'utilisateur de Firebase Auth
        await signOut(auth);
        return { 
          success: false, 
          error: 'Votre compte est en attente d\'activation. Veuillez contacter l\'administrateur.' 
        };
      }
      
      // Construire le nom d'affichage en gérant les deux formats (français et anglais)
      const firstName = userData.firstName || userData.prenom || '';
      const lastName = userData.lastName || userData.nom || '';
      const fullName = firstName && lastName 
        ? `${firstName} ${lastName}` 
        : firstName || lastName || userData.name || email.split('@')[0];
      
      return { 
        success: true, 
        user: {
          email: email,
          uid: userCredential.user.uid,
          name: userData.name || fullName,
          firstName: firstName,
          lastName: lastName,
          prenom: firstName,
          nom: lastName,
          username: userData.username || userData.name || fullName,
          role: userData.role === 'Client' ? 'Exportateur' : (userData.role || 'Exportateur'),
          isAdmin: userData.isAdmin === true,
          displayName: userData.name || userData.username || fullName,
          companyName: userData.companyName || null
        }
      };
    }
    
    // Si pas trouvé dans 'users', vérifier dans 'Providers'
    const providersRef = collection(db, 'Providers');
    const q = query(providersRef, where('email', '==', email));
    const querySnapshot = await getDocs(q);
    
    if (!querySnapshot.empty) {
      const providerDoc = querySnapshot.docs[0];
      const providerData = providerDoc.data();
      console.log('✅ Document Providers trouvé:', providerData);
      
      // Vérifier le statut du prestataire
      if (providerData.status === 'inactive') {
        console.log('❌ Compte prestataire inactif détecté');
        // Déconnecter l'utilisateur de Firebase Auth
        await signOut(auth);
        return { 
          success: false, 
          error: 'Votre compte est en attente d\'activation. Veuillez contacter l\'administrateur.' 
        };
      }
      
      // Sync isAdmin dans users pour que les règles Firestore reconnaissent les prestataires admin
      if (providerData.isAdmin === true) {
        try {
          const userDocRef = doc(db, 'users', email);
          await setDoc(userDocRef, { email, isAdmin: true, updatedAt: new Date().toISOString() }, { merge: true });
        } catch (syncErr) {
          console.warn('Sync isAdmin users (non bloquant):', syncErr?.message);
        }
      }
      
      // Construire le nom d'affichage pour les providers en gérant les deux formats
      const firstName = providerData.firstName || providerData.prenom || '';
      const lastName = providerData.lastName || providerData.nom || '';
      const fullName = firstName && lastName 
        ? `${firstName} ${lastName}` 
        : firstName || lastName || providerData.name || email.split('@')[0];
      
      return { 
        success: true, 
        user: {
          email: email,
          uid: userCredential.user.uid,
          name: providerData.name || fullName,
          firstName: firstName,
          lastName: lastName,
          prenom: firstName,
          nom: lastName,
          username: providerData.username || providerData.name || providerData.companyName || fullName,
          role: providerData.role || 'Prestataire',
          isAdmin: providerData.isAdmin === true,
          displayName: providerData.companyName || providerData.name || providerData.username || fullName,
          companyName: providerData.companyName || null
        }
      };
    }
    
    // Aucun compte trouvé dans Firestore
    console.log('❌ Aucun compte trouvé dans Firestore');
    return { success: false, error: 'Aucun compte trouvé avec cet email' };
    
  } catch (error) {
    console.error('❌ Erreur lors de la connexion:', error);
    return { success: false, error: 'Erreur de connexion. Veuillez réessayer.' };
  }
};

// Fonction de déconnexion hybride
export const logout = async () => {
  try {
    console.log('🔍 Déconnexion Firebase Auth...');
    await signOut(auth);
    console.log('✅ Déconnexion réussie');
    return { success: true };
  } catch (error) {
    console.error('❌ Erreur lors de la déconnexion:', error);
    return { success: false, error: error.message };
  }
};

// Fonction pour écouter les changements d'état d'authentification
export const onAuthStateChange = (callback) => {
  const { onAuthStateChanged } = require('firebase/auth');
  return onAuthStateChanged(auth, callback);
};

// Fonction pour obtenir l'utilisateur actuel
export const getCurrentUser = () => {
  return auth.currentUser;
};

// Fonction pour créer un utilisateur de test (à supprimer en production)
export const createTestUser = async (email, password, name) => {
  try {
    console.log('🔧 Création d\'utilisateur de test:', email);
    
    // Utiliser la fonction directe au lieu de l'API
    const result = await createTestUserDirect(email, password, name, 'Test', 'User');
    
    if (result.success) {
      console.log('✅ Utilisateur de test créé avec succès:', email);
      return { success: true };
    } else {
      console.error('❌ Erreur lors de la création de l\'utilisateur de test:', result.error);
      return { success: false, error: result.error };
    }
  } catch (error) {
    console.error('❌ Erreur lors de la création de l\'utilisateur de test:', error);
    return { success: false, error: error.message };
  }
};

// Fonction de test pour vérifier la connexion (à supprimer en production)
export const testConnection = async () => {
  try {
    console.log('=== TEST DE CONNEXION ===');
    
    // 1. Créer l'utilisateur de test
    console.log('1. Création de l\'utilisateur de test...');
    const createResult = await createTestUser('elo.zakaria@gmail.com', 'Ezikos2501!!', 'Zakaria Elo');
    console.log('Résultat de création:', createResult);
    
    // 2. Tester la connexion
    console.log('2. Test de connexion...');
    const loginResult = await loginWithEmailAndPassword('elo.zakaria@gmail.com', 'Ezikos2501!!');
    console.log('Résultat de connexion:', loginResult);
    
    return { success: true, createResult, loginResult };
  } catch (error) {
    console.error('Erreur lors du test de connexion:', error);
    return { success: false, error: error.message };
  }
};

// Fonction de test pour créer un utilisateur de test (à supprimer en production)
export const createTestUserDirect = async (email, password, name, firstName, lastName) => {
  try {
    console.log('🔧 Création d\'utilisateur de test direct:', email);
    
    const { setDoc } = await import('firebase/firestore');
    const userDocRef = doc(db, 'users', email);
    
    const userData = {
      email: email,
      password: password,
      name: name,
      firstName: firstName || 'Test',
      lastName: lastName || 'User',
      role: 'Exportateur',
      isAdmin: false,
      createdAt: new Date().toISOString(),
      status: 'active'
    };
    
    await setDoc(userDocRef, userData);
    console.log('✅ Utilisateur de test créé avec succès');
    return { success: true };
    
  } catch (error) {
    console.error('❌ Erreur lors de la création d\'utilisateur de test:', error);
    return { success: false, error: error.message };
  }
};

// Fonction de connexion principale - Version simplifiée
export const loginUser = async (email, password) => {
  try {
    console.log('🔍 Connexion directe via Firestore:', email);
    
    // Utiliser directement la fonction de connexion refactorisée
    const result = await loginWithEmailAndPassword(email, password);
    
    if (result.success) {
      console.log('✅ Connexion réussie via Firestore');
      return result;
    } else {
      console.error('❌ Erreur lors de la connexion via Firestore:', result.error);
      return { success: false, error: result.error };
    }
    
  } catch (error) {
    console.error('❌ Erreur lors de la connexion:', error);
    return {
      success: false,
      error: 'Erreur lors de la connexion'
    };
  }
};


// Fonction pour créer un prestataire de services de test
export const createTestProvider = async (email, password, companyName) => {
  try {
    console.log('Création d\'un prestataire de test:', email);
    
    // Utiliser l'API au lieu de Firestore directement
    const { createProviderAPI } = await import('../services/apiService');
    const result = await createProviderAPI(email, password, companyName);
    
    if (result.success) {
      console.log('✅ Prestataire de test créé via API');
      return result;
    } else {
      console.error('Erreur lors de la création du prestataire de test via API:', result.error);
      return { success: false, error: result.error };
    }
  } catch (error) {
    console.error('Erreur lors de la création du prestataire de test:', error);
    return { success: false, error: error.message };
  }
};
